import {
  EnumTagGroupKey,
  EnumTagGroupType,
  TagManyWithCountsQuery,
  useTagByIdQuery,
  useTagDeleteMutation,
} from "gql/generated";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { getMultilingualString } from "shared/utils";
import { useToastContext } from "@ttc3k/trekker";
import { InternalRefetchQueriesInclude } from "@apollo/client";
import { usePostHog } from "shared/hooks";

export type TagDetailsFlyoutMenuAction = "delete" | "split" | "merge";

export type UseTagDetailsFlyoutProps = {
  isOpen: boolean;
  onClose: () => void;
  selectedTagId: string;
  flyoutCategoryOptions: {
    value: string;
    label: string;
    key: EnumTagGroupKey;
    type: EnumTagGroupType;
  }[];
  onTagDeleted: (tag: TagManyWithCountsQuery["tagMany"][0]) => void;
  tagMutationCompleteRefreshesQueries?: InternalRefetchQueriesInclude;
};

export const useTagDetailsFlyout = ({
  isOpen,
  selectedTagId,
  onClose,
  onTagDeleted,
  tagMutationCompleteRefreshesQueries,
}: UseTagDetailsFlyoutProps) => {
  const { t } = useTranslation("adminland", {
    keyPrefix: "PAGE.TAG_MANAGER.FLYOUTS.TAG_DETAILS",
  });
  const { t: tCore } = useTranslation("core");
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  const { data, error, loading } = useTagByIdQuery({
    variables: { id: selectedTagId },
    skip: !isOpen || !selectedTagId,
  });

  const tag = data?.tagById;

  const [openModal, setOpenModal] = useState<TagDetailsFlyoutMenuAction | null>(
    null,
  );

  const [deleteTag, { loading: deleteTagLoading }] = useTagDeleteMutation();
  /**
   * Delete the tag
   * - Removes all instances of the tag across all Whereabouts entities
   * - Deletes the tag
   * - Updates the context (removes the deleted tag)
   * - Closes the flyout
   */
  const handleDeleteConfirmed = async () => {
    if (!tag) return;

    try {
      setOpenModal(null);

      const { data, errors } = await deleteTag({
        variables: { id: tag._id },
        ...(tagMutationCompleteRefreshesQueries
          ? { refetchQueries: tagMutationCompleteRefreshesQueries }
          : {}),
      });
      if (errors) {
        throw new Error(errors[0].message);
      }
      if (data?.tagDelete?.record) {
        toastFactory.create({
          title: tCore("SUCCESS"),
          description: t("SUCCESS_DELETE", {
            label: getMultilingualString(tag?.name ?? {}),
          }),
        });
        onTagDeleted(tag);
        onClose();
      }
    } catch (error) {
      trackPostHogException(error, {
        fileName: "useTagDetailsFlyout.ts",
        method: "handleDeleteConfirmed",
        message: "Error deleting tag",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ERRORS.TAG_DELETE"),
      });
    }
  };

  return {
    tag,
    error,
    loading,
    deleteTagLoading,
    openModal,
    setOpenModal,
    onDeleteConfirmed: handleDeleteConfirmed,
  };
};
